import { useEffect, useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { takeAuthNotice, tryError } from "../lib/api";
import { getRecaptchaToken, preloadRecaptcha } from "../lib/recaptcha";
import RecaptchaNotice from "../components/RecaptchaNotice";
import { AlertCircle, Loader2, ArrowRight, Sparkles, LifeBuoy, Globe, Mail, Phone } from "lucide-react";
import WhatsAppIcon from "../components/WhatsAppIcon";

const BG_IMAGE =
  "https://images.unsplash.com/photo-1533134486753-c833f0ed4866?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwyfHxkYXJrJTIwYWJzdHJhY3QlMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc3NzgxMDgwM3ww&ixlib=rb-4.1.0&q=85";
const ESNEK_LOGO = "https://www.esnekyazilim.com.tr/wp-content/uploads/2023/01/EsnekYazilimLighLogo.png";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  // Tek aktif oturum (şartname #3): oturum başka bir cihazdan devralındığında
  // api.js bu mesajı sessionStorage'a yazıp buraya yönlendiriyor.
  const [notice, setNotice] = useState("");

  useEffect(() => {
    setNotice(takeAuthNotice());
    preloadRecaptcha();
  }, []);

  if (user) {
    return <Navigate to={user.role === "master" ? "/master" : "/"} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      // Görünmez reCAPTCHA v3 token'ı — anahtar yapılandırılmamışsa null döner
      // ve backend doğrulamayı atlar.
      const captcha = await getRecaptchaToken("login");
      const data = await login(email.trim(), password, captcha);
      navigate(data.role === "master" ? "/master" : "/");
    } catch (err) {
      setError(tryError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-foreground text-background">
      <div
        className="relative hidden lg:flex flex-col justify-between p-10"
        style={{
          backgroundImage: `linear-gradient(rgba(15,23,42,0.65), rgba(15,23,42,0.92)), url('${BG_IMAGE}')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="flex items-center gap-4">
          <img src={ESNEK_LOGO} alt="Esnek Yazılım" className="h-12 w-auto object-contain" />
          <div className="space-y-1">
            <div className="font-display font-black text-2xl tracking-tighter">
              LASTİK OTELİ <span className="text-gray-400 text-sm align-top ml-1">SaaS</span>
            </div>
            <div className="text-xs uppercase tracking-[0.28em] text-white">Lastik Oteli Yönetim Sistemi</div>
          </div>
        </div>

        <div className="max-w-md space-y-6">
          <h1 className="font-display font-black text-5xl xl:text-6xl tracking-tighter leading-[0.95]">
            Lastik Otelinizi,<br />
            <span className="text-sky-400 whitespace-nowrap">Tek Panelden Yönetin</span>
          </h1>
          <p className="text-base text-background/70 leading-relaxed max-w-sm">
            Müşteri, emanet lastikler, depo ve sezon işlemlerini tek sistemde takip edin.
          </p>
          <div className="grid grid-cols-3 gap-px bg-background/15 mt-10">
            {[
              { k: "Hızlı", v: "QR Kod" },
              { k: "Anlık", v: "Takip" },
              { k: "Mobil", v: "Uyumlu" },
            ].map((s) => (
              <div key={s.k} className="bg-foreground p-4">
                <div className={`font-display font-black text-2xl ${s.k === "Mobil" ? "text-white" : "text-white"}`}>{s.k}</div>
                <div className={`label-eyebrow mt-1 ${s.k === "Mobil" ? "text-white" : "text-white"}`}>{s.v}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="text-xs text-background/50 font-mono">
          © Esnek Yazılım – Tüm hakları saklıdır.
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10 bg-background text-foreground">
        <div className="w-full max-w-md space-y-8">
          <div className="lg:hidden flex items-center gap-3 mb-4">
            <img
              src="/esnek-icon.png"
              alt="Esnek Yazılım"
              className="h-10 w-10 rounded-sm object-contain shrink-0"
            />
            <div className="font-display font-black text-xl tracking-tighter">LASTİK OTELİ</div>
          </div>

          <div>
            <div className="label-eyebrow text-amber-600">Giriş</div>
            <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mt-2">
              Hesabınıza giriş yapın
            </h2>
            <p className="text-sm text-muted-foreground mt-3">
              Yönetim paneline erişmek için bilgilerinizi girin.
            </p>
          </div>

          {/* Tek aktif oturum uyarısı (şartname #3) */}
          {notice && (
            <div
              className="flex items-start gap-3 border-l-2 border-amber-500 bg-amber-50 px-4 py-3 text-sm text-amber-900"
              data-testid="session-takeover-notice"
              role="status"
            >
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
              <div>
                <div className="font-semibold">Oturumunuz sona erdirildi</div>
                <div className="mt-0.5 text-amber-800">{notice}</div>
              </div>
            </div>
          )}

          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="label-eyebrow">E-posta</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="username"
                className="h-12 rounded-sm border-foreground/30 focus:border-foreground focus:ring-foreground"
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="label-eyebrow">Şifre</Label>
                <Link to="/sifre-unuttum" className="text-xs text-amber-600 hover:underline" data-testid="forgot-link">
                  Şifremi unuttum
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="h-12 rounded-sm border-foreground/30 focus:border-foreground focus:ring-foreground"
                data-testid="login-password-input"
              />
            </div>

            {error && (
              <div
                className="border-l-2 border-destructive bg-destructive/10 px-4 py-3 text-sm text-destructive"
                data-testid="login-error"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-12 rounded-sm bg-slate-900 text-white hover:bg-sky-600 group"
              data-testid="login-submit-btn"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <span className="flex items-center justify-center gap-2 font-semibold tracking-tight">
                  Panele Giriş Yap
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </span>
              )}
            </Button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div>
              <div className="relative flex justify-center"><span className="bg-background px-3 text-xs text-muted-foreground uppercase tracking-wider">veya</span></div>
            </div>

            <Button
              type="button"
              asChild
              variant="outline"
              className="w-full h-12 rounded-sm border-foreground/30 hover:bg-foreground hover:text-background group"
              data-testid="signup-cta"
            >
              <Link to="/signup">
                <span className="flex items-center justify-center gap-2 font-semibold tracking-tight">
                  Ücretsiz Deneyin
                  <Sparkles className="h-4 w-4 text-amber-600 group-hover:text-amber-400" />
                </span>
              </Link>
            </Button>

            {/* Rozet gizlendiği için Google'ın istediği bilgilendirme metni */}
            <RecaptchaNotice className="text-center" />

            {/* Support info */}
            <div className="mt-10 pt-8 border-t border-border">
              <div className="flex items-start gap-3">
                <div className="h-9 w-9 bg-amber-500 rounded-sm flex items-center justify-center shrink-0">
                  <LifeBuoy className="h-4 w-4 text-foreground" />
                </div>
                <div className="flex-1">
                  <div className="text-xs font-mono space-y-1">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                      <span className="text-amber-600 font-semibold">Bilgi ve Destek İçin:</span>
                      <a
                        href="mailto:info@esnekyazilim.com.tr"
                        className="inline-flex items-center gap-1 hover:text-amber-600 transition-colors"
                      >
                        <Mail className="h-3 w-3 text-amber-600" />
                        info@esnekyazilim.com.tr
                      </a>
                      <span className="text-border">•</span>
                      <a
                        href="tel:+905317932454"
                        className="inline-flex items-center gap-1 hover:text-amber-600 transition-colors"
                      >
                        <Phone className="h-3 w-3 text-amber-600" />
                        +90 531 793 24 54
                      </a>
                      <span className="text-border">•</span>
                      <a
                        href="https://wa.me/905317932454"
                        target="_blank"
                        rel="noopener noreferrer"
                        title="WhatsApp ile yaz"
                        className="inline-flex items-center gap-1 text-emerald-700 hover:text-emerald-800 transition-colors"
                      >
                        <WhatsAppIcon className="h-3 w-3" />
                        WhatsApp
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>


        </div>
      </div>
    </div>
  );
}
